import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  Timestamp,
} from "firebase/firestore";
import { db } from "./init";
import { Tag } from "@/types/tag";
import { convertTimestampToDate } from "./firestore";

/**
 * Tag ID 驗證規則
 */
export const TAG_ID_REGEX = /^[a-z0-9_]+$/;
export const TAG_ID_MIN_LENGTH = 1;

/**
 * 驗證 Tag ID 格式
 * @param tagId - 要驗證的 Tag ID
 * @returns 驗證結果，包含 valid 和可選的 error 訊息
 */
export function validateTagId(tagId: string): { valid: boolean; error?: string } {
  if (!tagId || tagId.trim().length < TAG_ID_MIN_LENGTH) {
    return { valid: false, error: "Tag ID 為必填" };
  }
  if (!TAG_ID_REGEX.test(tagId)) {
    return { valid: false, error: "Tag ID 只能包含小寫字母、數字和底線" };
  }
  return { valid: true };
}

/**
 * Firestore 標籤數據類型（包含 Timestamp）
 */
interface FirestoreTagData {
  tag_id?: string;
  zh?: string;
  tw?: string;
  en?: string;
  ms?: string;
  created_at?: Timestamp | Date | null;
  updated_at?: Timestamp | Date | null;
  createdAt?: Timestamp | Date | null;
  updatedAt?: Timestamp | Date | null;
}

/**
 * 轉換標籤數據（處理 Timestamp）
 */
function convertTagData(data: FirestoreTagData, id: string): Omit<Tag, "id"> {
  return {
    tag_id: data.tag_id || id,
    zh: data.zh || "",
    tw: data.tw || "",
    en: data.en || "",
    ms: data.ms || "",
    created_at: convertTimestampToDate(data.created_at || data.createdAt) as Date | undefined,
    updated_at: convertTimestampToDate(data.updated_at || data.updatedAt) as Date | undefined,
    createdAt: convertTimestampToDate(data.createdAt || data.created_at) as Date | undefined,
    updatedAt: convertTimestampToDate(data.updatedAt || data.updated_at) as Date | undefined,
  };
}

/**
 * 獲取所有標籤
 */
export async function getTags(): Promise<Tag[]> {
  try {
    const tagsRef = collection(db, "tags");
    let querySnapshot;
    let useClientSort = false;
    
    try {
      const q = query(tagsRef, orderBy("tw", "asc"));
      querySnapshot = await getDocs(q);
    } catch (orderError) {
      // 如果 orderBy 失敗（可能沒有索引），使用基本查詢並在客戶端排序
      console.warn("Failed to order by 'tw', using basic query with client-side sort:", orderError);
      querySnapshot = await getDocs(query(tagsRef));
      useClientSort = true;
    }

    const tags = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...convertTagData(doc.data(), doc.id),
    })) as Tag[];

    // 如果沒有使用 orderBy，在客戶端排序
    if (useClientSort) {
      tags.sort((a, b) => (a.tw || "").localeCompare(b.tw || ""));
    }

    return tags;
  } catch (error) {
    console.error("Error getting tags:", error);
    throw error;
  }
}

/**
 * 獲取單個標籤
 */
export async function getTag(id: string): Promise<Tag | null> {
  try {
    const tagRef = doc(db, "tags", id);
    const tagSnap = await getDoc(tagRef);

    if (tagSnap.exists()) {
      return {
        id: tagSnap.id,
        ...convertTagData(tagSnap.data(), tagSnap.id),
      } as Tag;
    }

    return null;
  } catch (error) {
    console.error("Error getting tag:", error);
    throw error;
  }
}

/**
 * 創建標籤
 * 使用 tag_id 作為文檔 ID
 */
export async function createTag(
  data: Omit<Tag, "id" | "created_at" | "updated_at" | "createdAt" | "updatedAt">
): Promise<string> {
  try {
    // 驗證 tag_id 格式
    if (!data.tag_id) throw new Error("Tag ID 為必填");
    const tagIdValidation = validateTagId(data.tag_id);
    if (!tagIdValidation.valid) {
      throw new Error(tagIdValidation.error || "Tag ID 驗證失敗");
    }

    // 驗證所有語言欄位
    if (!data.zh || !data.tw || !data.en || !data.ms) {
      throw new Error("所有語言欄位都是必填");
    }

    // 檢查標籤是否已存在
    const existingTag = await getTag(data.tag_id);
    if (existingTag) {
      throw new Error("此 Tag ID 已存在");
    }

    const tagRef = doc(db, "tags", data.tag_id);
    const tagData: {
      tag_id: string;
      zh: string;
      tw: string;
      en: string;
      ms: string;
      created_at: Timestamp;
      updated_at: Timestamp;
    } = {
      tag_id: data.tag_id,
      zh: data.zh,
      tw: data.tw,
      en: data.en,
      ms: data.ms,
      created_at: Timestamp.now(),
      updated_at: Timestamp.now(),
    };

    await setDoc(tagRef, tagData);
    return data.tag_id;
  } catch (error) {
    console.error("Error creating tag:", error);
    throw error;
  }
}

/**
 * 更新標籤
 */
export async function updateTag(
  id: string,
  data: Partial<Omit<Tag, "id" | "tag_id" | "created_at" | "createdAt">>
): Promise<void> {
  try {
    const tagRef = doc(db, "tags", id);
    const updateData: {
      updated_at: Timestamp;
      zh?: string;
      tw?: string;
      en?: string;
      ms?: string;
    } = {
      updated_at: Timestamp.now(),
    };

    // 只更新提供的欄位
    if (data.zh !== undefined) updateData.zh = data.zh;
    if (data.tw !== undefined) updateData.tw = data.tw;
    if (data.en !== undefined) updateData.en = data.en;
    if (data.ms !== undefined) updateData.ms = data.ms;

    // 驗證所有語言欄位（如果更新了任何一個，確保所有都不為空）
    const currentDoc = await getDoc(tagRef);
    if (currentDoc.exists()) {
      const currentData = currentDoc.data();
      const finalZh = data.zh !== undefined ? data.zh : currentData.zh;
      const finalTw = data.tw !== undefined ? data.tw : currentData.tw;
      const finalEn = data.en !== undefined ? data.en : currentData.en;
      const finalMs = data.ms !== undefined ? data.ms : currentData.ms;

      if (!finalZh || !finalTw || !finalEn || !finalMs) {
        throw new Error("所有語言欄位都是必填");
      }
    }

    await updateDoc(tagRef, updateData);
  } catch (error) {
    console.error("Error updating tag:", error);
    throw error;
  }
}

/**
 * 刪除標籤
 * 刪除前會檢查是否有劇集正在使用此標籤
 */
export async function deleteTag(id: string): Promise<void> {
  try {
    // 檢查是否有劇集使用此標籤
    const tagRef = doc(db, "tags", id);
    const dramasRef = collection(db, "dramas");
    
    // 查詢所有包含此標籤的劇集
    const q = query(dramasRef, where("tags", "array-contains", tagRef));
    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      const count = snapshot.size;
      throw new Error(
        `無法刪除標籤：仍有 ${count} 個劇集正在使用此標籤。請先移除劇集中的標籤引用。`
      );
    }
    
    // 沒有劇集使用此標籤，可以安全刪除
    await deleteDoc(tagRef);
  } catch (error) {
    // 檢查是否為 Firestore 索引錯誤
    const firestoreError = error as { code?: string; message?: string };
    if (firestoreError?.code === "failed-precondition") {
      throw new Error(
        "查詢需要 Firestore 索引。請在 Firebase Console 中建立索引：dramas 集合的 tags 欄位需要 array-contains 索引。"
      );
    }
    console.error("Error deleting tag:", error);
    throw error;
  }
}

